import { Link } from 'react-router-dom';
import {
  ChevronDown,
  CreditCard,
  FileText,
  Headphones,
  Package,
  ScanLine,
  type LucideIcon,
} from 'lucide-react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Seo } from '../components/Seo';
import { FAQ_CATEGORIES, type FaqCategoryIcon } from '../lib/faqData';

const FAQ_ICONS: Record<FaqCategoryIcon, LucideIcon> = {
  scan: ScanLine,
  fileText: FileText,
  creditCard: CreditCard,
  package: Package,
  headphones: Headphones,
};

export function FaqPage() {
  return (
    <>
      <Seo
        path="/faq"
        title="Veelgestelde vragen (FAQ)"
        description="Antwoorden over de ComfortTool™-scan, rapporten, Comfort Basis en Plus, betalen, account en digitale levering."
        keywords="ComfortTool FAQ, scan, koellast, warmtelast, rapport, account, betalen, installateur"
      />
      <div className="mx-auto max-w-3xl px-4 py-10 sm:py-14">
        <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'FAQ' }]} />
        <header className="mt-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-ct-accent">Support</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-ct-text sm:text-4xl">
            Veelgestelde vragen
          </h1>
          <p className="mt-4 text-pretty text-base leading-relaxed text-ct-muted sm:text-lg">
            Klik een vraag open voor het antwoord. Staat je vraag er niet tussen? Probeer{' '}
            <Link to="/troubleshooting" className="font-semibold text-ct-accent hover:underline">
              Troubleshooting
            </Link>
            , de installatieadviseur of{' '}
            <Link to="/contact" className="font-semibold text-ct-accent hover:underline">
              contact
            </Link>
            .
          </p>
        </header>

        <div className="ct-stagger-children mt-12 space-y-12">
          {FAQ_CATEGORIES.map((cat) => {
            const Icon = FAQ_ICONS[cat.icon];
            return (
              <section key={cat.id} aria-labelledby={`faq-${cat.id}`}>
                <h2
                  id={`faq-${cat.id}`}
                  className="flex items-center gap-3 text-xl font-bold text-ct-text"
                >
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-ct-accent/12 text-ct-accent"
                    aria-hidden
                  >
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </span>
                  {cat.title}
                </h2>
                <div className="mt-5 space-y-3">
                  {cat.items.map((item) => (
                    <details
                      key={item.q}
                      className="group ct-hover-surface rounded-2xl border border-ct-border bg-ct-surface/80 px-5 py-4 shadow-sm open:border-ct-accent/35 open:shadow-md"
                    >
                      <summary className="flex cursor-pointer list-none items-start justify-between gap-3 font-semibold text-ct-text marker:content-none [&::-webkit-details-marker]:hidden">
                        <span className="min-w-0 flex-1">{item.q}</span>
                        <ChevronDown
                          className="mt-0.5 h-5 w-5 shrink-0 text-ct-muted transition-transform duration-200 group-open:rotate-180"
                          aria-hidden
                        />
                      </summary>
                      <div className="mt-3 border-t border-ct-border/60 pt-3 text-sm leading-relaxed text-ct-muted">
                        {item.a}
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        <div className="ct-hover-surface mt-14 rounded-2xl border border-ct-accent/25 bg-ct-accent/5 p-6 text-center">
          <p className="text-sm font-medium text-ct-text">Klaar om te starten?</p>
          <Link
            to="/scan"
            className="mt-3 inline-flex items-center justify-center rounded-xl bg-ct-accent px-6 py-3 text-sm font-bold text-white transition hover:bg-blue-500"
          >
            Naar de scan
          </Link>
        </div>
      </div>
    </>
  );
}
